/**
 * Where each finding of the Policy Review Guide sits in the policy text.
 *
 * The guide reports per section heading; the editor renders per paragraph.
 * This maps a finding to the paragraph it is about, so the note appears in
 * the body of the policy under that paragraph, never in a block above it.
 * Pure text matching, no model call.
 */
const norm = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** The paragraph index a section heading refers to, or 0 when it is the whole policy. */
export function anchorIndex(heading, paragraphs) {
  const paras = Array.isArray(paragraphs) ? paragraphs : [];
  if (!paras.length) return 0;
  const nh = norm(heading);
  if (!nh || nh === 'full policy' || nh.startsWith('remaining sections') || nh === 'opening') return 0;
  const headings = paras.filter((p) => p.heading && norm(p.text));
  const exact = headings.find((p) => norm(p.text) === nh);
  if (exact) return exact.index;
  const contains = headings.find((p) => norm(p.text).includes(nh) || nh.includes(norm(p.text)));
  if (contains) return contains.index;
  const anyPara = paras.find((p) => norm(p.text) && (norm(p.text).includes(nh) || nh.includes(norm(p.text))));
  if (anyPara) return anyPara.index;
  // "Section 3: Monitoring" style headings: match on the words after the colon.
  const tail = nh.includes(' ') ? nh.split(' ').slice(-3).join(' ') : nh;
  const byTail = headings.find((p) => norm(p.text).includes(tail));
  return byTail ? byTail.index : 0;
}

/**
 * Inline notes for the editor: { [paragraphIndex]: [{ label, heading, note, citations, applies, condition, tone }] }.
 * Flags and unverified observations are placed; clean sections place nothing.
 * `skipIndexes` leaves out paragraphs that already carry suggested wording.
 */
export function anchorFindings(sections, paragraphs, { skipIndexes = null, skipHeadings = null, by = null } = {}) {
  const out = {};
  const nh = (t) => String(t || '').toLowerCase().trim();
  for (const s of Array.isArray(sections) ? sections : []) {
    if (!s || (s.status !== 'flag' && s.status !== 'unverified')) continue;
    // A finding that already placed suggested wording says nothing twice.
    if (skipHeadings && skipHeadings.has(nh(s.heading))) continue;
    const idx = anchorIndex(s.heading, paragraphs);
    if (skipIndexes && skipIndexes.has(idx)) continue;
    const item = {
      label: s.status === 'flag' ? (s.applies === 'conditional' ? 'Depends on your answer' : 'Needs attention') : 'Observation, not verified',
      tone: s.status === 'flag' ? 'amber' : 'slate',
      heading: s.heading || '',
      note: s.note || '',
      caveat: s.caveat || '',
      citations: Array.isArray(s.citations) ? s.citations : [],
      condition: s.condition || null,
      by: by || null,
    };
    (out[idx] = out[idx] || []).push(item);
  }
  return out;
}

/** Compliance's returned edits as inline notes for the owner: adds and erasures the editor cannot show as wording. */
export function anchorReturnedEdits(edits, by = null) {
  const out = {};
  for (const e of Array.isArray(edits) ? edits : []) {
    if (!e || e.op === 'replace') continue;
    const idx = Number(e.index);
    if (!Number.isInteger(idx)) continue;
    const item = e.op === 'delete'
      ? { label: `${by || 'Compliance'} suggests erasing this paragraph`, tone: 'red', note: '', citations: [] }
      : { label: `${by || 'Compliance'} suggests adding after this paragraph`, tone: 'emerald', note: String(e.after || ''), citations: [] };
    (out[idx] = out[idx] || []).push(item);
  }
  return out;
}
